import React, { useState, useEffect } from 'react';
import { Card, Button, Typography, Breadcrumb, Row, Col, Space, Tag, } from 'antd';
import { showConfirmModal } from '../components/PopupModal';
import { useNavigate, useParams } from 'react-router-dom';
import { GreetingItem, getExposureDisplayText } from '../types/greeting';
import greetingService from '../services/greeting';
import { message } from '../utils/message';
import GreetingPreviewModal from '../components/GreetingPreviewModal';

const { Title, Text } = Typography;

const GreetingDetailPage: React.FC = () => {
  const navigate = useNavigate();
  const { id: greetingId } = useParams();

  const [greetingDetail, setGreetingDetail] = useState<GreetingItem | null>(null);
  const [loading, setLoading] = useState(true);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [previewVisible, setPreviewVisible] = useState(false);  // 미리보기 모달

  // 그리팅 상세 정보 로드
  useEffect(() => {
    // console.log('useEffect 실행됨, greetingId:', greetingId);

    const loadGreetingDetail = async () => {
      if (!greetingId) {
        // console.log('greetingId가 없음');
        message.error('그리팅 ID가 필요합니다.');
        navigate('/greeting-management');
        return;
      }

      // console.log('API 호출 시작, greetingId:', greetingId);
      setLoading(true);
      try {
        const response = await greetingService.getGreetingDetail(Number(greetingId));
        // console.log('API 응답:', response);
        setGreetingDetail(response);
      } catch (error) {
        console.error('API 에러:', error);
        message.error('그리팅 정보를 불러오는 중 오류가 발생했습니다.');
        navigate('/greeting-management');
      } finally {
        // console.log('로딩 완료');
        setLoading(false);
      }
    };

    loadGreetingDetail();
  }, [greetingId, navigate]);

  // 뒤로 가기
  const handleBack = () => {
    navigate('/greeting-management');
  };

  // 미리보기 버튼
  const handlePreview = () => {
    setPreviewVisible(true);
  };

  // 수정 버튼
  const handleEdit = () => {
    navigate(`/greeting-edit/${greetingId}`);
  };

  // 삭제 버튼
  const handleDelete = () => {
    if (!greetingDetail) return;

    showConfirmModal({
      title: '알림',
      content: '삭제한 콘텐츠는 복구할 수 없습니다.\n삭제하시겠습니까?',
      okText: '확인',
      okType: 'danger',
      onConfirm: async () => {
        try {
          setDeleteLoading(true);
          await greetingService.deleteGreeting(greetingDetail.id);
          message.success('삭제가 완료되었습니다.');
          navigate('/greeting-management');
        } catch (error) {
          console.error('그리팅 삭제 실패:', error);
          message.error('그리팅 삭제 중 오류가 발생했습니다.');
        } finally {
          setDeleteLoading(false);
        }
      },
    });
  };

  // console.log('렌더링 상태 - loading:', loading, 'greetingDetail:', greetingDetail);

  if (loading) {
    // console.log('로딩 상태 렌더링');
    return (
      <div style={{ padding: '20px', textAlign: 'center', marginTop: '100px', backgroundColor: '#000', minHeight: '200px' }}>
        <Text style={{ color: '#ffffff', fontSize: '16px' }}>로딩 중...</Text>
      </div>
    );
  }

  if (!greetingDetail) {
    // console.log('데이터 없음 상태 렌더링');
    return (
      <div style={{ padding: '20px', textAlign: 'center', marginTop: '100px', backgroundColor: '#000', minHeight: '200px' }}>
        <Text style={{ color: '#ffffff', fontSize: '16px' }}>그리팅 정보를 찾을 수 없습니다.</Text>
      </div>
    );
  }

  // console.log('메인 컨텐츠 렌더링');

  return (
    <Space direction="vertical" size="large" style={{ width: '100%' }}>
      {/* 브레드크럼 */}
      <Breadcrumb
        items={[
          { title: '콘텐츠 관리' },
          { title: '미디어 아트' },
          { title: '그리팅', href: '/greeting-management' },
          { title: '그리팅 상세' },
        ]}
        style={{ marginBottom: '8px' }}
      />

      {/* 페이지 헤더 */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        margin: '0 0 32px 0'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
          <Title level={2} style={{ color: '#ffffff', margin: 0 }}>
            그리팅 상세
          </Title>
        </div>
        <Button onClick={handlePreview}>
          미리보기
        </Button>
      </div>

      {/* 기본 정보 섹션 */}
      <Card
        title={<Text style={{ color: '#ffffff', fontSize: '16px', fontWeight: 'medium' }}>기본 정보</Text>}
        style={{
          background: '#1f1f1f',
          border: '1px solid #303030',
        }}
        styles={{ body: { padding: '24px' } }}
      >
        <Row gutter={[32, 16]}>
          <Col span={12}>
            <div style={{ marginBottom: '24px' }}>
              <Text style={{ fontWeight: 'bold', color: '#a6a6a6', fontSize: '14px', display: 'block', marginBottom: '8px' }}>
                레이아웃명
              </Text>
              <Text style={{ color: '#ffffff', fontSize: '16px' }}>
                {greetingDetail.layoutName || '-'}
              </Text>
            </div>
          </Col>
        </Row>
        <Row gutter={[32, 16]}>
          <Col span={12}>
            <div style={{ marginBottom: '24px' }}>
              <Text style={{ fontWeight: 'bold', color: '#a6a6a6', fontSize: '14px', display: 'block', marginBottom: '8px' }}>
                노출여부
              </Text>
              <Text>
                {getExposureDisplayText(greetingDetail.exposureType)}
              </Text>
            </div>
          </Col>
        </Row>
        <Row gutter={[32, 16]}>
          <Col span={24}>
            <div style={{ marginBottom: '24px' }}>
              <Text style={{ fontWeight: 'bold', color: '#a6a6a6', fontSize: '14px', display: 'block', marginBottom: '8px' }}>
                노출 기간
              </Text>
              <Text style={{ color: '#ffffff', fontSize: '16px' }}>
                {greetingDetail.startDate && greetingDetail.endDate
                  ? (() => {
                    const startDateStr = new Date(greetingDetail.startDate).toLocaleDateString('ko-KR', {
                      year: 'numeric',
                      month: '2-digit',
                      day: '2-digit',
                    }).replace(/\//g, '. ');
                    const endDateStr = new Date(greetingDetail.endDate).toLocaleDateString('ko-KR', {
                      year: 'numeric',
                      month: '2-digit',
                      day: '2-digit',
                    }).replace(/\//g, '. ');

                    // 시간 정보가 있으면 괄호로 추가
                    const timeStr = greetingDetail.startTime && greetingDetail.endTime
                      ? ` (${greetingDetail.startTime.split(':')[0]}시~${greetingDetail.endTime.split(':')[0]}시)`
                      : '';

                    return `${startDateStr}~ ${endDateStr}${timeStr}`;
                  })()
                  : '상시노출'}
              </Text>
            </div>
          </Col>
        </Row>
      </Card>

      {/* 그리팅 정보 섹션 */}
      <Card
        title={<Text style={{ color: '#ffffff', fontSize: '16px', fontWeight: 'medium' }}>그리팅 정보</Text>}
        style={{
          background: '#1f1f1f',
          border: '1px solid #303030',
        }}
        styles={{ body: { padding: '24px' } }}
      >
        <Row gutter={[32, 16]}>
          <Col span={24}>
            <div style={{ marginBottom: '24px' }}>
              <Text style={{ fontWeight: 'bold', color: '#a6a6a6', fontSize: '14px', display: 'block', marginBottom: '8px' }}>
                제목
              </Text>
              <Text style={{ color: '#ffffff', fontSize: '16px' }}>
                {greetingDetail.title}
              </Text>
            </div>
          </Col>
          <Col span={12}>
            <div style={{ marginBottom: '24px' }}>
              <Text style={{ fontWeight: 'bold', color: '#a6a6a6', fontSize: '14px', display: 'block', marginBottom: '8px' }}>
                방문 입주사명
              </Text>
              <Text style={{ color: '#ffffff', fontSize: '16px' }}>
                {greetingDetail.visitorCompanyName || '-'}
              </Text>
            </div>
          </Col>
          <Col span={24}>
            <div style={{ marginBottom: '24px' }}>
              <Text style={{ fontWeight: 'bold', color: '#a6a6a6', fontSize: '14px', display: 'block', marginBottom: '8px' }}>
                내용
              </Text>
              <div style={{
                padding: '16px',
                border: '1px solid #303030',
                borderRadius: '6px',
                background: '#141414',
                minHeight: '80px',
              }}>
                {Array.isArray(greetingDetail.content) ? (
                  greetingDetail.content.map((item, index) => (
                    <Text key={index} style={{
                      color: '#ffffff',
                      fontSize: '14px',
                      lineHeight: '1.6',
                      wordBreak: 'break-all',
                      display: 'block',
                      marginBottom: index < greetingDetail.content.length - 1 ? '8px' : 0
                    }}>
                      {item}
                    </Text>
                  ))
                ) : (
                  <Text style={{
                    color: '#ffffff',
                    fontSize: '14px',
                    lineHeight: '1.6',
                    wordBreak: 'break-all'
                  }}>
                    {greetingDetail.content}
                  </Text>
                )}
              </div>
            </div>
          </Col>
        </Row>
      </Card>

      {/* 등록 정보 섹션 */}
      <Card
        title={<Text style={{ color: '#ffffff', fontSize: '16px', fontWeight: 'medium' }}>등록 정보</Text>}
        style={{
          background: '#1f1f1f',
          border: '1px solid #303030',
        }}
        styles={{ body: { padding: '24px' } }}
      >
        <Row gutter={[32, 16]}>
          <Col span={12}>
            <div style={{ marginBottom: '24px' }}>
              <Text style={{ fontWeight: 'bold', color: '#a6a6a6', fontSize: '14px', display: 'block', marginBottom: '8px' }}>
                등록일
              </Text>
              <Text style={{ color: '#ffffff', fontSize: '16px' }}>
                {new Date(greetingDetail.createdAt).toLocaleDateString('ko-KR')}
              </Text>
            </div>
          </Col>
        </Row>
        <Row gutter={[32, 16]}>
          <Col span={12}>
            <div style={{ marginBottom: '24px' }}>
              <Text style={{ fontWeight: 'bold', color: '#a6a6a6', fontSize: '14px', display: 'block', marginBottom: '8px' }}>
                최근 수정일
              </Text>
              <Text style={{ color: '#ffffff', fontSize: '16px' }}>
                {greetingDetail.updatedAt ? new Date(greetingDetail.updatedAt).toLocaleDateString('ko-KR') : '-'}
              </Text>
            </div>
          </Col>
        </Row>
      </Card>

      {/* 액션 버튼 */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginTop: '32px'
      }}>
        <Button
          danger
          size="large"
          onClick={handleDelete}
          loading={deleteLoading}
          style={{
            minWidth: '100px'
          }}
        >
          삭제
        </Button>

        <Button
          type="primary"
          size="large"
          onClick={handleEdit}
          style={{
            minWidth: '100px',
            background: '#F24822',
            border: 'none'
          }}
        >
          수정
        </Button>
      </div>

      {/* 미리보기 모달 */}
      {greetingDetail && (
        <GreetingPreviewModal
          visible={previewVisible}
          onClose={() => setPreviewVisible(false)}
          greeting={greetingDetail}
        />
      )}
    </Space>
  );
};

export default GreetingDetailPage;